import { IconBulbFilled, IconPencil } from "@tabler/icons-react";
import { Tabs, Button, Accordion } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import "./ReviewPage.scss";
import { useState, useEffect } from "react";
import { useTaxOrganizerStore } from "../../../../../utils/stores/taxOrganizerStore";
import { tabsRenderConfig } from "./tabsRenderConfig";
import { useShallow } from "zustand/shallow";

const ReviewPage = () => {
  const [error, setError] = useState<string>("");
  const isMobile = useMediaQuery("(max-width: 768px)");

  const {
    responses,
    steps,
    taxYear,
    setCurrentStep,
    setCurrentPage,
    setEditingReviewPage,
  } = useTaxOrganizerStore(
    useShallow((state: any) => ({
      responses: state.responses,
      steps: state.steps,
      taxYear: state.taxYear,
      setCurrentStep: state.setCurrentStep,
      setCurrentPage: state.setCurrentPage,
      setEditingReviewPage: state.setEditingReviewPage,
    }))
  );

  useEffect(() => {
    setEditingReviewPage(false);
    try {
      if (!steps || Object.keys(steps).length === 0) {
        throw new Error("Failed to load steps data.");
      }
      if (!responses || Object.keys(responses).length === 0) {
        throw new Error("Failed to load responses data.");
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unknown error occurred");
      }
    }
  }, [responses, steps]);

  const filteredSteps = Object.keys(steps).filter(
    (step) =>
      step !== "Review" && step !== "Disclosure Agreement" && step !== "welcome"
  );

  const displayStepName = (step: string) => {
    return step === "Getting Started" ? "Overview" : step;
  };

  const handleNavigate = (step: string) => {
    setCurrentStep(step);
    setCurrentPage(steps[step][0]);
    setEditingReviewPage(true);
  };

  const renderTabContent = (step: string) => {
    const content = tabsRenderConfig[step]?.render
      ? tabsRenderConfig[step].render!(responses)
      : steps[step]?.map(
          (page: any) =>
            tabsRenderConfig.default?.renderPage?.(
              step,
              page,
              responses,
              taxYear
            ) ?? <div key={page}>Error rendering content</div>
        );

    return (
      <div className="tab-content-container">
        <div className="tab-content-btns">
          <Button
            className="navigate-button"
            c="gray"
            variant="transparent"
            onClick={() => handleNavigate(step)}
            leftSection={<IconPencil size={24} />}
          >
            Edit
          </Button>
        </div>
        <div className="review-content">{content}</div>
      </div>
    );
  };

  const renderMobileView = () => (
    <Accordion
      multiple
      defaultValue={filteredSteps}
      className="review-accordion"
      variant="separated"
    >
      {filteredSteps.map((step) => (
        <Accordion.Item key={step} value={step}>
          <Accordion.Control>{displayStepName(step)}</Accordion.Control>
          <Accordion.Panel>{renderTabContent(step)}</Accordion.Panel>
        </Accordion.Item>
      ))}
    </Accordion>
  );

  const renderDesktopView = () => (
    <Tabs defaultValue={filteredSteps[0]} className="review-tabs">
      <Tabs.List>
        {filteredSteps.map((step) => (
          <Tabs.Tab key={step} value={step}>
            {displayStepName(step)}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      {filteredSteps.map((step) => (
        <Tabs.Panel key={step} value={step}>
          {renderTabContent(step)}
        </Tabs.Panel>
      ))}
    </Tabs>
  );

  return (
    <div className="review-container">
      <p>
        You're nearly at the end! Review all the information you've submitted
        and make sure it's accurate.
      </p>
      <div className="review-tip">
        <IconBulbFilled size={24} className="review-tip-icon" />
        <p>Make sure you review all the sections</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="review-section">
        {!error && (isMobile ? renderMobileView() : renderDesktopView())}
      </div>
    </div>
  );
};

export default ReviewPage;
